import { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import ImageResize from 'quill-image-resize-module-react';
import {
  Box,
  Button,
  HStack,
  VStack,
  Text,
  List,
  ListItem,
  ListIcon,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Checkbox,
} from '@chakra-ui/react';
import {
  AttachmentIcon,
  CloseIcon,
  AddIcon,
  MinusIcon,
} from '@chakra-ui/icons';
import {
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFilePowerpoint,
  FaFileAlt,
  FaFileImage,
} from 'react-icons/fa';
import { ImageCropModal } from './ImageCropModal';
import 'react-quill/dist/quill.snow.css';

// Register custom table blot to preserve HTML tables
const BlockEmbed = Quill.import('blots/block/embed') as any;
const Parchment = Quill.import('parchment') as any;

class TableBlot extends BlockEmbed {
  static blotName = 'table-container';
  static tagName = 'div';
  static className = 'table-container';
  static scope = Parchment.Scope.BLOCK_BLOT;

  static create(value: any) {
    const node = super.create(value) as HTMLElement;
    node.setAttribute('contenteditable', 'false');
    node.setAttribute('data-quill-ignore', 'true');

    if (typeof value === 'string') {
      node.innerHTML = value;
    }
    // Make table cells editable
    const cells = node.querySelectorAll('td, th');
    cells.forEach((cell) => {
      (cell as HTMLElement).setAttribute('contenteditable', 'true');
    });

    node.addEventListener(
      'keydown',
      (e: KeyboardEvent) => {
        const target = e.target as HTMLElement;
        const isInCell =
          target.tagName === 'TD' ||
          target.tagName === 'TH' ||
          target.closest('td') ||
          target.closest('th');
        if (isInCell) {
          e.stopPropagation();
        }
      },
      true,
    );

    node.addEventListener(
      'input',
      (e: Event) => {
        e.stopPropagation();
      },
      true,
    );

    node.addEventListener(
      'beforeinput',
      (e: Event) => {
        e.stopPropagation();
      },
      true,
    );

    node.addEventListener(
      'compositionstart',
      (e: Event) => {
        e.stopPropagation();
      },
      true,
    );
    node.addEventListener(
      'compositionend',
      (e: Event) => {
        e.stopPropagation();
      },
      true,
    );

    return node;
  }

  static value(node: HTMLElement) {
    return node.innerHTML;
  }

  length() {
    return 1;
  }

  update(mutations: MutationRecord[], context: any) {
    // Filter out mutations that happen inside table cells
    const filteredMutations = mutations.filter((mutation) => {
      let target = mutation.target;

      if (target.nodeType === Node.TEXT_NODE) {
        target = target.parentElement || target.parentNode;
      }

      // Check if target is an element with the closest method
      if (target && (target as HTMLElement).closest) {
        const isInsideTable =
          (target as HTMLElement).closest('table') ||
          (target as HTMLElement).tagName === 'TABLE' ||
          (target as HTMLElement).tagName === 'TD' ||
          (target as HTMLElement).tagName === 'TH' ||
          (target as HTMLElement).tagName === 'TR' ||
          (target as HTMLElement).tagName === 'TBODY';
        return !isInsideTable;
      }
      return true;
    });
    if (filteredMutations.length > 0) {
      super.update(filteredMutations, context);
    }
  }
}

// Register custom iframe blot to preserve iframes
class IframeBlot extends BlockEmbed {
  static blotName = 'iframe';
  static tagName = 'iframe';

  static create(value: any) {
    const node = super.create(value) as HTMLIFrameElement;

    if (typeof value === 'string') {
      node.setAttribute('src', value);
    } else if (typeof value === 'object') {
      // Support full iframe attributes
      Object.keys(value).forEach((key) => {
        node.setAttribute(key, value[key]);
      });
    }

    // Set default attributes for security and styling
    if (!node.getAttribute('frameborder')) {
      node.setAttribute('frameborder', '0');
    }
    if (!node.getAttribute('style')) {
      node.setAttribute(
        'style',
        'border: none; max-width:100%; width: 640px; height: 480px;',
      );
    }

    return node;
  }

  static value(node: HTMLIFrameElement) {
    // Return all attributes to preserve the full iframe
    const attributes: any = {};
    Array.from(node.attributes).forEach((attr) => {
      attributes[attr.name] = attr.value;
    });
    return attributes;
  }
}

// Register the modules
try {
  Quill.register('modules/imageResize', ImageResize);
  Quill.register(TableBlot);
  Quill.register(IframeBlot);
} catch (error) {
  console.warn('Failed to register modules:', error);
}

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onFilesChange?: (files: File[]) => void;
}

export const RichTextEditor = ({
  value,
  onChange,
  placeholder = 'Write your notification content here...',
  onFilesChange,
}: RichTextEditorProps) => {
  const quillRef = useRef<ReactQuill>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const onChangeRef = useRef(onChange);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [includeHeader, setIncludeHeader] = useState(false);
  const [selectedCell, setSelectedCell] = useState<HTMLElement | null>(null);
  const [toolbarPosition, setToolbarPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [iframeUrl, setIframeUrl] = useState('');
  const [iframeWidth, setIframeWidth] = useState('640px');
  const [iframeHeight, setIframeHeight] = useState('480px');

  const {
    isOpen: isCropOpen,
    onOpen: onCropOpen,
    onClose: onCropClose,
  } = useDisclosure();
  const {
    isOpen: isTableOpen,
    onOpen: onTableOpen,
    onClose: onTableClose,
  } = useDisclosure();
  const {
    isOpen: isIframeOpen,
    onOpen: onIframeOpen,
    onClose: onIframeClose,
  } = useDisclosure();

  // Keep onChangeRef up to date
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Table insertion handler
  const insertTable = useCallback(
    (rows: number = 3, cols: number = 3, includeHeader: boolean = false) => {
      const quill = quillRef.current?.getEditor();
      if (!quill) {
        console.error('Quill editor not found');
        return;
      }

      // Generate table HTML with inline styles
      let tableHTML =
        '<table style="border-collapse: collapse; width: 100%; margin: 10px 0; border: 1px solid #ddd;"><tbody>';

      if (includeHeader && rows > 0) {
        // Create header row with <th> elements
        tableHTML += '<tr>';
        for (let j = 0; j < cols; j++) {
          tableHTML +=
            '<th contenteditable="true" style="border: 1px solid #ddd; padding: 8px; min-width: 100px; height: 30px; background-color: #f5f5f5; font-weight: bold;">Header ' +
            (j + 1) +
            '</th>';
        }
        tableHTML += '</tr>';

        // Create remaining rows with <td> elements
        for (let i = 1; i < rows; i++) {
          tableHTML += '<tr>';
          for (let j = 0; j < cols; j++) {
            tableHTML +=
              '<td contenteditable="true" style="border: 1px solid #ddd; padding: 8px; min-width: 100px; height: 30px;">&nbsp;</td>';
          }
          tableHTML += '</tr>';
        }
      } else {
        // Create all rows with <td> elements (original behavior)
        for (let i = 0; i < rows; i++) {
          tableHTML += '<tr>';
          for (let j = 0; j < cols; j++) {
            tableHTML +=
              '<td contenteditable="true" style="border: 1px solid #ddd; padding: 8px; min-width: 100px; height: 30px;">&nbsp;</td>';
          }
          tableHTML += '</tr>';
        }
      }
      tableHTML += '</tbody></table>';

      // Get cursor position
      const range = quill.getSelection(true);
      const position = range ? range.index : quill.getLength();

      // Insert the table using the custom blot
      quill.insertEmbed(position, 'table-container', tableHTML, 'user');

      // Insert a line break after the table
      quill.insertText(position + 1, '\n', 'user');

      // Move cursor after the table
      setTimeout(() => {
        quill.setSelection(position + 2, 0);
      }, 10);
    },
    [],
  );

  // Custom image handler with crop option
  const imageHandler = useCallback(() => {
    imageInputRef.current?.click();
  }, []);

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target?.result as string;
        setImageToCrop(base64);
        onCropOpen();
      };
      reader.readAsDataURL(file);
    }
    // Reset input
    event.target.value = '';
  };

  const handleCroppedImage = (croppedBase64: string) => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection();
      quill.insertEmbed(range?.index || 0, 'image', croppedBase64);
    }
  };

  // Table handler to open modal
  const tableHandler = useCallback(() => {
    onTableOpen();
  }, [onTableOpen]);

  // Handle table insertion from modal
  const handleTableInsert = () => {
    insertTable(tableRows, tableCols, includeHeader);
    setIncludeHeader(false); // Reset for next time
    onTableClose();
  };

  // Iframe handler to open modal
  const iframeHandler = useCallback(() => {
    onIframeOpen();
  }, [onIframeOpen]);

  // Handle iframe insertion from modal
  const handleIframeInsert = () => {
    if (!iframeUrl) return;

    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const range = quill.getSelection(true);
    const position = range ? range.index : quill.getLength();

    let finalUrl = iframeUrl;
    let finalWidth = iframeWidth;
    let finalHeight = iframeHeight;
    let additionalAttrs: any = {};

    // Check if user pasted full iframe HTML
    if (iframeUrl.includes('<iframe')) {
      // Parse the iframe HTML to extract attributes
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = iframeUrl;
      const iframeElement = tempDiv.querySelector('iframe');

      if (iframeElement) {
        finalUrl = iframeElement.getAttribute('src') || '';
        finalWidth = iframeElement.getAttribute('width') || iframeWidth;
        finalHeight = iframeElement.getAttribute('height') || iframeHeight;

        // Extract other attributes
        Array.from(iframeElement.attributes).forEach((attr) => {
          if (!['src', 'width', 'height'].includes(attr.name)) {
            additionalAttrs[attr.name] = attr.value;
          }
        });
      }
    }

    // Insert the iframe with all attributes
    const iframeAttributes = {
      src: finalUrl,
      width: finalWidth,
      height: finalHeight,
      frameborder: '0',
      style: 'border: none; max-width:100%;',
      allowfullscreen: 'true',
      webkitallowfullscreen: 'true',
      mozallowfullscreen: 'true',
      msallowfullscreen: 'true',
      ...additionalAttrs,
    };

    quill.insertEmbed(position, 'iframe', iframeAttributes, 'user');

    // Insert a line break after the iframe
    quill.insertText(position + 1, '\n', 'user');

    // Move cursor after the iframe
    setTimeout(() => {
      quill.setSelection(position + 2, 0);
    }, 10);

    // Reset and close
    setIframeUrl('');
    setIframeWidth('640px');
    setIframeHeight('480px');
    onIframeClose();
  };

  // Table manipulation functions
  const addRow = useCallback(
    (position: 'above' | 'below' = 'below') => {
      if (!selectedCell) return;
      const row = selectedCell.closest('tr');
      const table = selectedCell.closest('table');
      if (!row || !table) return;

      const newRow = document.createElement('tr');
      const cells = row.querySelectorAll('td, th');

      cells.forEach((cell) => {
        const newCell = document.createElement(cell.tagName.toLowerCase());
        newCell.contentEditable = 'true';
        newCell.style.cssText = (cell as HTMLElement).style.cssText;
        newCell.innerHTML = '&nbsp;';
        newRow.appendChild(newCell);
      });

      if (position === 'below') {
        row.after(newRow);
      } else {
        row.before(newRow);
      }
    },
    [selectedCell],
  );

  const deleteRow = useCallback(() => {
    if (!selectedCell) return;
    const row = selectedCell.closest('tr');
    const table = selectedCell.closest('table');
    if (!row || !table) return;

    // Don't delete if it's the only row
    const rows = table.querySelectorAll('tr');
    if (rows.length <= 1) return;

    row.remove();
    setSelectedCell(null);
    setToolbarPosition(null);
  }, [selectedCell]);

  const addColumn = useCallback(
    (position: 'left' | 'right' = 'right') => {
      if (!selectedCell) return;
      const table = selectedCell.closest('table');
      if (!table) return;

      const rows = table.querySelectorAll('tr');
      const cellIndex = Array.from(
        selectedCell.closest('tr')!.children,
      ).indexOf(selectedCell);

      rows.forEach((row) => {
        const cells = row.children;
        const cell = cells[cellIndex] as HTMLElement;
        const newCell = document.createElement(cell.tagName.toLowerCase());
        newCell.contentEditable = 'true';
        newCell.style.cssText = cell.style.cssText;
        newCell.innerHTML = '&nbsp;';

        if (position === 'right') {
          cell.after(newCell);
        } else {
          cell.before(newCell);
        }
      });
    },
    [selectedCell],
  );

  const deleteColumn = useCallback(() => {
    if (!selectedCell) return;
    const table = selectedCell.closest('table');
    if (!table) return;

    const cellIndex = Array.from(selectedCell.closest('tr')!.children).indexOf(
      selectedCell,
    );
    const rows = table.querySelectorAll('tr');

    // Check if any row has only one cell
    const hasSingleCellRow = Array.from(rows).some(
      (row) => row.children.length <= 1,
    );
    if (hasSingleCellRow) return;

    rows.forEach((row) => {
      const cell = row.children[cellIndex];
      if (cell) cell.remove();
    });

    setSelectedCell(null);
    setToolbarPosition(null);
  }, [selectedCell]);

  const toggleHeaderRow = useCallback(() => {
    if (!selectedCell) return;
    const table = selectedCell.closest('table');
    if (!table) return;

    const row = selectedCell.closest('tr');
    if (!row) return;

    const cells = row.querySelectorAll('td, th');
    const isHeader = row.querySelector('th') !== null;

    cells.forEach((cell) => {
      const newCell = document.createElement(isHeader ? 'td' : 'th');
      newCell.contentEditable = 'true';
      newCell.style.cssText = (cell as HTMLElement).style.cssText;
      newCell.innerHTML = cell.innerHTML;

      if (!isHeader) {
        newCell.style.backgroundColor = '#f5f5f5';
        newCell.style.fontWeight = 'bold';
      } else {
        newCell.style.backgroundColor = '';
        newCell.style.fontWeight = '';
      }

      cell.replaceWith(newCell);
    });
  }, [selectedCell]);

  // Add tooltips and table button SVG icon
  useEffect(() => {
    const toolbar = document.querySelector('.ql-toolbar');
    if (toolbar) {
      // Add tooltips using title attribute (native browser tooltips)
      const buttons: { [key: string]: string } = {
        '.ql-bold': 'Bold (Ctrl+B)',
        '.ql-italic': 'Italic (Ctrl+I)',
        '.ql-underline': 'Underline (Ctrl+U)',
        '.ql-strike': 'Strikethrough',
        '.ql-list[value="ordered"]': 'Numbered List',
        '.ql-list[value="bullet"]': 'Bullet List',
        '.ql-indent[value="-1"]': 'Decrease Indent',
        '.ql-indent[value="+1"]': 'Increase Indent',
        '.ql-link': 'Insert Link',
        '.ql-image': 'Insert Image (resizable by dragging corners)',
        '.ql-table': 'Insert Table (3x3)',
        '.ql-iframe': 'Insert Iframe (Embed external content)',
        '.ql-blockquote': 'Block Quote',
        '.ql-code-block': 'Code Block',
        '.ql-clean': 'Clear Formatting',
      };

      Object.entries(buttons).forEach(([selector, tooltip]) => {
        const button = toolbar.querySelector(selector);
        if (button) {
          button.setAttribute('title', tooltip);
        }
      });

      // Add SVG icon to table button
      const tableButton = toolbar.querySelector('.ql-table') as HTMLElement;
      if (tableButton && !tableButton.querySelector('svg')) {
        const svg = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'svg',
        );
        svg.setAttribute('viewBox', '0 0 18 18');
        svg.style.display = 'inline-block';
        svg.style.verticalAlign = 'middle';

        const rect1 = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'rect',
        );
        rect1.setAttribute('class', 'ql-stroke');
        rect1.setAttribute('height', '12');
        rect1.setAttribute('width', '12');
        rect1.setAttribute('x', '3');
        rect1.setAttribute('y', '3');

        const rect2 = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'rect',
        );
        rect2.setAttribute('class', 'ql-fill');
        rect2.setAttribute('height', '2');
        rect2.setAttribute('width', '4');
        rect2.setAttribute('x', '5');
        rect2.setAttribute('y', '5');

        const rect3 = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'rect',
        );
        rect3.setAttribute('class', 'ql-fill');
        rect3.setAttribute('height', '2');
        rect3.setAttribute('width', '4');
        rect3.setAttribute('x', '10');
        rect3.setAttribute('y', '5');

        const rect4 = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'rect',
        );
        rect4.setAttribute('class', 'ql-fill');
        rect4.setAttribute('height', '2');
        rect4.setAttribute('width', '4');
        rect4.setAttribute('x', '5');
        rect4.setAttribute('y', '9');

        const rect5 = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'rect',
        );
        rect5.setAttribute('class', 'ql-fill');
        rect5.setAttribute('height', '2');
        rect5.setAttribute('width', '4');
        rect5.setAttribute('x', '10');
        rect5.setAttribute('y', '9');

        svg.appendChild(rect1);
        svg.appendChild(rect2);
        svg.appendChild(rect3);
        svg.appendChild(rect4);
        svg.appendChild(rect5);

        tableButton.appendChild(svg);
      }

      // Add SVG icon to iframe button
      const iframeButton = toolbar.querySelector('.ql-iframe') as HTMLElement;
      if (iframeButton && !iframeButton.querySelector('svg')) {
        const svg = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'svg',
        );
        svg.setAttribute('viewBox', '0 0 18 18');
        svg.style.display = 'inline-block';
        svg.style.verticalAlign = 'middle';

        // Create a code/window icon for iframe
        const rect1 = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'rect',
        );
        rect1.setAttribute('class', 'ql-stroke');
        rect1.setAttribute('height', '14');
        rect1.setAttribute('width', '14');
        rect1.setAttribute('x', '2');
        rect1.setAttribute('y', '2');
        rect1.setAttribute('rx', '2');

        const line1 = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'line',
        );
        line1.setAttribute('class', 'ql-stroke');
        line1.setAttribute('x1', '5');
        line1.setAttribute('y1', '7');
        line1.setAttribute('x2', '8');
        line1.setAttribute('y2', '10');

        const line2 = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'line',
        );
        line2.setAttribute('class', 'ql-stroke');
        line2.setAttribute('x1', '8');
        line2.setAttribute('y1', '10');
        line2.setAttribute('x2', '5');
        line2.setAttribute('y2', '13');

        const line3 = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'line',
        );
        line3.setAttribute('class', 'ql-stroke');
        line3.setAttribute('x1', '10');
        line3.setAttribute('y1', '10');
        line3.setAttribute('x2', '13');
        line3.setAttribute('y2', '7');

        const line4 = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'line',
        );
        line4.setAttribute('class', 'ql-stroke');
        line4.setAttribute('x1', '13');
        line4.setAttribute('y1', '13');
        line4.setAttribute('x2', '10');
        line4.setAttribute('y2', '10');

        svg.appendChild(rect1);
        svg.appendChild(line1);
        svg.appendChild(line2);
        svg.appendChild(line3);
        svg.appendChild(line4);

        iframeButton.appendChild(svg);
      }

      // Add tooltip for header dropdown
      const headerPicker = toolbar.querySelector('.ql-header');
      if (headerPicker) {
        headerPicker.setAttribute('title', 'Text Style');
      }

      // Add tooltip for align dropdown
      const alignPicker = toolbar.querySelector('.ql-align');
      if (alignPicker) {
        alignPicker.setAttribute('title', 'Text Alignment');
      }

      // Add tooltip for color picker
      const colorPicker = toolbar.querySelector('.ql-color');
      if (colorPicker) {
        colorPicker.setAttribute('title', 'Text Color');
      }

      // Add tooltip for background picker
      const backgroundPicker = toolbar.querySelector('.ql-background');
      if (backgroundPicker) {
        backgroundPicker.setAttribute('title', 'Background Color');
      }
    }
  }, []);

  // Handle table cell selection and toolbar positioning
  useEffect(() => {
    const handleTableCellClick = (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'TD' || target.tagName === 'TH') {
        event.stopPropagation();
        setSelectedCell(target);

        // Position toolbar above the cell
        const rect = target.getBoundingClientRect();
        // Use the editor root element
        const editor = quillRef.current?.getEditor();
        const editorRoot = editor?.root;
        const editorRect = editorRoot?.getBoundingClientRect();
        if (editorRect) {
          setToolbarPosition({
            top: rect.top - editorRect.top - 40,
            left: rect.left - editorRect.left,
          });
        }
      } else if (!target.closest('table')) {
        // Click outside table, clear selection
        setSelectedCell(null);
        setToolbarPosition(null);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;

      const isInTableCell =
        target.tagName === 'TD' ||
        target.tagName === 'TH' ||
        target.closest('td') ||
        target.closest('th');

      if (isInTableCell) {
        if (e.key === 'Backspace' || e.key === 'Delete') {
          e.stopPropagation();
          return;
        }
      }

      if (selectedCell && (e.key === 'Escape' || e.key === 'Tab')) {
        setSelectedCell(null);
        setToolbarPosition(null);
      }
    };

    const editor = quillRef.current?.getEditor();
    if (editor) {
      editor.root.addEventListener('click', handleTableCellClick);
      editor.root.addEventListener('keydown', handleKeyDown, true);
    }

    return () => {
      if (editor) {
        editor.root.removeEventListener('click', handleTableCellClick);
        editor.root.removeEventListener('keydown', handleKeyDown, true);
      }
    };
  }, [selectedCell]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        // PowerPoint files
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        // Text files
        'text/plain',
        // Rich Text Format
        'application/rtf',
        'text/rtf',
        // OpenDocument formats
        'application/vnd.oasis.opendocument.text',
        'application/vnd.oasis.opendocument.spreadsheet',
        'application/vnd.oasis.opendocument.presentation',
        // Image files
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/bmp',
        'image/webp',
        'image/svg+xml',
      ];
      const newFiles = Array.from(files).filter((file) =>
        allowedTypes.includes(file.type),
      );
      setAttachedFiles((prev) => [...prev, ...newFiles]);
      if (onFilesChange) {
        onFilesChange([...attachedFiles, ...newFiles]);
      }
    }
    // Reset input
    event.target.value = '';
  };

  const removeFile = (index: number) => {
    const updated = attachedFiles.filter((_, i) => i !== index);
    setAttachedFiles(updated);
    if (onFilesChange) {
      onFilesChange(updated);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileType: string) => {
    if (fileType === 'application/pdf') return FaFilePdf;
    if (fileType.includes('word') || fileType.includes('opendocument.text'))
      return FaFileWord;
    if (
      fileType.includes('excel') ||
      fileType.includes('spreadsheet') ||
      fileType.includes('opendocument.spreadsheet')
    )
      return FaFileExcel;
    if (
      fileType.includes('powerpoint') ||
      fileType.includes('presentation') ||
      fileType.includes('opendocument.presentation')
    )
      return FaFilePowerpoint;
    if (fileType.includes('text/plain') || fileType.includes('rtf'))
      return FaFileAlt;
    if (fileType.startsWith('image/')) return FaFileImage;
    return FaFilePdf; // fallback to PDF icon
  };

  const getFileIconColor = (fileType: string) => {
    if (fileType === 'application/pdf') return 'red.500';
    if (fileType.includes('word') || fileType.includes('opendocument.text'))
      return 'blue.500';
    if (
      fileType.includes('excel') ||
      fileType.includes('spreadsheet') ||
      fileType.includes('opendocument.spreadsheet')
    )
      return 'green.500';
    if (
      fileType.includes('powerpoint') ||
      fileType.includes('presentation') ||
      fileType.includes('opendocument.presentation')
    )
      return 'orange.500'; // Orange for PowerPoint
    if (fileType.includes('text/plain') || fileType.includes('rtf'))
      return 'gray.500'; // Gray for text files
    if (fileType.startsWith('image/')) return 'purple.500'; // Purple for images
    return 'red.500'; // fallback
  };

  const modules = useMemo(() => {
    const config: any = {
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ indent: '-1' }, { indent: '+1' }],
          [{ align: [] }],
          ['link', 'image', 'table', 'iframe'],
          [{ color: [] }, { background: [] }],
          ['blockquote', 'code-block'],
          ['clean'],
        ],
        handlers: {
          image: imageHandler,
          table: tableHandler,
          iframe: iframeHandler,
        },
      },
      clipboard: {
        matchVisual: false,
      },
      keyboard: {
        bindings: {
          // Override backspace to prevent deleting entire table when inside a cell
          backspace: {
            key: 'Backspace',
            handler: function (this: any) {
              const selection = window.getSelection();
              const activeElement = document.activeElement;

              // Check if we're inside a table cell
              const isInTableCell =
                activeElement?.closest('td') ||
                activeElement?.closest('th') ||
                selection?.anchorNode?.parentElement?.closest('td') ||
                selection?.anchorNode?.parentElement?.closest('th');

              if (isInTableCell) {
                // Let native contenteditable handle it, don't let Quill process it
                return true;
              }
              return true;
            },
          },
          delete: {
            key: 'Delete',
            handler: function (this: any) {
              const selection = window.getSelection();
              const activeElement = document.activeElement;

              // Check if we're inside a table cell
              const isInTableCell =
                activeElement?.closest('td') ||
                activeElement?.closest('th') ||
                selection?.anchorNode?.parentElement?.closest('td') ||
                selection?.anchorNode?.parentElement?.closest('th');

              if (isInTableCell) {
                return true;
              }
              return true;
            },
          },
        },
      },
    };

    // Add image resize if available
    try {
      config.imageResize = {
        parchment: Quill.import('parchment'),
        modules: ['Resize', 'DisplaySize'],
      };
    } catch (error) {
      console.warn('Image resize module not available:', error);
    }

    return config;
  }, [imageHandler, tableHandler, iframeHandler]);

  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'indent',
    'align',
    'link',
    'image',
    'color',
    'background',
    'blockquote',
    'code-block',
    'width',
    'height',
    'style',
    'table-container',
    'iframe',
  ];

  // Table Toolbar Component
  const TableToolbar = () => {
    if (!selectedCell || !toolbarPosition) return null;

    return (
      <Box
        position="absolute"
        top={`${toolbarPosition.top}px`}
        left={`${toolbarPosition.left}px`}
        bg="white"
        border="1px solid #ddd"
        borderRadius="4px"
        p={1}
        boxShadow="md"
        zIndex={1000}
        display="flex"
        gap={1}
        style={{ position: 'absolute', zIndex: 1000 }}
      >
        <HStack spacing={1} wrap="wrap">
          {/* Row operations */}
          <Button
            size="xs"
            leftIcon={<AddIcon />}
            onClick={() => addRow('above')}
            title="Add row above"
            aria-label="Add row above"
          >
            Row↑
          </Button>

          <Button
            size="xs"
            leftIcon={<AddIcon />}
            onClick={() => addRow('below')}
            title="Add row below"
            aria-label="Add row below"
          >
            Row↓
          </Button>

          <Button
            size="xs"
            leftIcon={<MinusIcon />}
            onClick={deleteRow as any}
            title="Delete row"
            aria-label="Delete row"
          >
            Del Row
          </Button>

          {/* Column operations */}
          <Button
            size="xs"
            leftIcon={<AddIcon />}
            onClick={() => addColumn('left')}
            title="Add column left"
            aria-label="Add column left"
          >
            Col←
          </Button>

          <Button
            size="xs"
            leftIcon={<AddIcon />}
            onClick={() => addColumn('right')}
            title="Add column right"
            aria-label="Add column right"
          >
            Col→
          </Button>

          <Button
            size="xs"
            leftIcon={<MinusIcon />}
            onClick={deleteColumn as any}
            title="Delete column"
            aria-label="Delete column"
          >
            Del Col
          </Button>

          {/* Header toggle */}
          <Button size="xs" onClick={toggleHeaderRow as any}>
            Header
          </Button>
        </HStack>
      </Box>
    );
  };

  return (
    <VStack align="stretch" spacing={4}>
      <Box
        position="relative"
        borderRadius="md"
        overflow="hidden"
        sx={{
          '.ql-container': {
            minHeight: '300px',
            fontSize: '16px',
            fontFamily: 'inherit',
            borderRadius: 'inherit',
          },
          '.ql-toolbar': {
            borderTopLeftRadius: '0.375rem',
            borderTopRightRadius: '0.375rem',
          },
          '.ql-editor': {
            minHeight: '300px',
          },
          '.ql-editor img': {
            maxWidth: '100%',
            height: 'auto',
            cursor: 'pointer',
          },
          '.ql-editor .table-container': {
            margin: '10px 0',
            padding: '10px',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            backgroundColor: '#fafafa',
          },
          '.ql-editor .table-container table': {
            borderCollapse: 'collapse',
            width: '100%',
            backgroundColor: 'white',
          },
          '.ql-editor .table-container table td': {
            border: '1px solid #ddd',
            padding: '8px',
            minWidth: '50px',
          },
          '.ql-editor .table-container table th': {
            border: '1px solid #ddd',
            padding: '8px',
            minWidth: '50px',
            backgroundColor: '#f5f5f5',
            fontWeight: 'bold',
            textAlign: 'left',
          },
          '.ql-editor .table-container table tr:nth-child(even)': {
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
          },
          '.ql-snow .ql-picker': {
            color: 'inherit',
          },
          // Enable tooltips for toolbar buttons
          '.ql-snow .ql-tooltip': {
            zIndex: 1000,
          },
          '.ql-toolbar button': {
            cursor: 'pointer',
          },
          '.ql-toolbar button:hover': {
            color: '#06c',
          },
          '.ql-toolbar .ql-table svg': {
            width: '18px',
            height: '18px',
          },
        }}
      >
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={onChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
        />
      </Box>

      {/* Hidden file inputs */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleImageSelect}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.odt,.ods,.odp,.jpg,.jpeg,.png,.gif,.bmp,.webp,.svg"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />

      {/* File Attachments Section */}
      <Box>
        <HStack mb={2}>
          <Button
            leftIcon={<AttachmentIcon />}
            size="sm"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
          >
            Attach Additional Files
          </Button>
          <Text fontSize="sm" color="gray.600">
            (Optional - Additional files will be downloadable attachments)
          </Text>
        </HStack>

        {attachedFiles.length > 0 && (
          <List spacing={2} mt={2}>
            {attachedFiles.map((file, index) => (
              <ListItem
                key={index}
                p={2}
                borderWidth="1px"
                borderRadius="md"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <HStack>
                  <ListIcon
                    as={getFileIcon(file.type)}
                    color={getFileIconColor(file.type)}
                  />
                  <VStack align="start" spacing={0}>
                    <Text fontSize="sm" fontWeight="medium">
                      {file.name}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {formatFileSize(file.size)}
                    </Text>
                  </VStack>
                </HStack>
                <IconButton
                  aria-label="Remove file"
                  icon={<CloseIcon />}
                  size="sm"
                  variant="ghost"
                  onClick={() => removeFile(index)}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* Table Configuration Modal */}
      <Modal isOpen={isTableOpen} onClose={onTableClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Insert Table</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Number of Rows</FormLabel>
                <NumberInput
                  min={1}
                  max={20}
                  value={tableRows}
                  onChange={(_, value) => setTableRows(value)}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl>
                <FormLabel>Number of Columns</FormLabel>
                <NumberInput
                  min={1}
                  max={10}
                  value={tableCols}
                  onChange={(_, value) => setTableCols(value)}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl>
                <Checkbox
                  isChecked={includeHeader}
                  onChange={(e: any) => setIncludeHeader(e.target.checked)}
                >
                  Include header row
                </Checkbox>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onTableClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleTableInsert}>
              Insert Table
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Iframe Configuration Modal */}
      <Modal isOpen={isIframeOpen} onClose={onIframeClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Insert Iframe</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Iframe URL</FormLabel>
                <Input
                  placeholder="https://example.com or https://forms.microsoft.com/..."
                  value={iframeUrl}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setIframeUrl(e.target.value)
                  }
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Paste the full iframe src URL or the complete iframe HTML code
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel>Width</FormLabel>
                <Input
                  placeholder="640px or 100%"
                  value={iframeWidth}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setIframeWidth(e.target.value)
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel>Height</FormLabel>
                <Input
                  placeholder="480px"
                  value={iframeHeight}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setIframeHeight(e.target.value)
                  }
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onIframeClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleIframeInsert}
              isDisabled={!iframeUrl}
            >
              Insert Iframe
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Image Crop Modal */}
      {imageToCrop && (
        <ImageCropModal
          isOpen={isCropOpen}
          onClose={() => {
            onCropClose();
            setImageToCrop(null);
          }}
          imageSrc={imageToCrop}
          onCropComplete={handleCroppedImage}
        />
      )}

      {/* Table Toolbar */}
      <TableToolbar />
    </VStack>
  );
};
